import React, { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import useApplePay from "../../hooks/use-apple-pay";

import { PaymentSystem } from "../../constants";
import { getPaymentSystem } from "../../utils/storage";

import classes from "./style.module.css";
import { useRemoteConfig } from "@web-solutions/base-app/hooks/use-remote-config";

/**
 * Цвет кнопки Apple задаётся свойством `-apple-pay-button-style`, и держать его
 * в CSS нельзя: минификатор прод-сборки переписывает ключевые слова `black` /
 * `white` в `#000` / `#fff`, а Safari принимает только ключевые слова — правило
 * молча отбрасывалось. Инлайн-стили не минифицируются, поэтому ставим свойство
 * из JS; `important` — чтобы значение не перебило правило со страницы.
 *
 * Тему читаем у ближайшего родителя с `data-theme`, а не сразу у `:root`: попап
 * оплаты остаётся белым при любой теме и помечает себя `data-theme="light"` —
 * на нём нужна чёрная кнопка, а на чёрном фоне страницы белая.
 */
const applyApplePayButtonStyle = (button) => {
  if (!button) {
    return;
  }

  const scope = button.closest("[data-theme]") || document.documentElement;
  const isDark = scope.getAttribute("data-theme") === "dark";

  button.style.setProperty(
    "-apple-pay-button-style",
    isDark ? "white" : "black",
    "important"
  );
};

export const ApplePayButton = ({
  product,
  label,
  disabled,
  className,
  onClick,
  onSubmit,
  onReady,
}) => {
  const paymentSystem = getPaymentSystem();
  const { showApplePayAnimation, collectEmailAfterPayment, requestApplePayEmail } = useRemoteConfig();
  const [address, setAddress] = useState();

  // Stripe deferred-email flow: when enabled, the Apple Pay charge needs no email
  // (collected afterwards). `requestApplePayEmail` decides whether we still ASK for
  // it in the native sheet (to skip the email-after-payment page when present).
  const emailOptional =
    Boolean(collectEmailAfterPayment) && paymentSystem === PaymentSystem.STRIPE;
  const requestPayerEmail = emailOptional && Boolean(requestApplePayEmail);

  const { id, amount, isIntroductory, trial_price_amount, currency } = product;

  const options = useMemo(
    () => ({
      label,
      amount: !isIntroductory ? amount : trial_price_amount,
      currency,
      requestPayerEmail,
    }),
    [amount, isIntroductory, label, trial_price_amount, currency, requestPayerEmail]
  );

  const { paymentRequest } = useApplePay(options, {
    onShippingAddress: (res) => {
      setAddress(res);
    },
    onMethod: (res) => {
      const formData = {
        ...(product.isOneTimePurchase ? { amount, product_id: product?.product_id, product_code: id, } : { price_id: id, product_id: id, }),
        isOneTimePurchase: product.isOneTimePurchase,
        method: 'applepay',
        shipping_address: address,
        currency,
      };

      if (paymentSystem === PaymentSystem.RECURLY) {
        if (res?.id) {
          formData.token_id = res.id;
        }
      } else if (paymentSystem === PaymentSystem.STRIPE) {
        const { trialDays, onetimePriceId } = product;

        const { complete, paymentMethod } = res;
        formData.payment_method_id = paymentMethod?.id;
        formData.trial_period_days = trialDays;
        if (isIntroductory && onetimePriceId) {
          formData.introductory_price_id = onetimePriceId;
        }
        // Deferred-email flow: stash the email Apple Pay returned (if any) so the
        // success handler can attach it and skip the email-after-payment page.
        // Set on every authorization (incl. cleared) to avoid stale data.
        if (emailOptional && typeof window !== "undefined") {
          window.__apple_pay_payer_email__ = res.payerEmail || undefined;
        }
        complete("success");
      } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
        Object.assign(formData, res);
      }

      onSubmit(formData);
    },
  });

  // `paymentRequest` is non-null only once the payment system confirmed the
  // device can pay, i.e. exactly when this button becomes visible. Signal that
  // once so the page can stop showing its preloader (without this the caller's
  // `onReady` is never called and the loader hangs forever).
  const readyNotifiedRef = useRef(false);
  useEffect(() => {
    if (paymentRequest && !readyNotifiedRef.current) {
      readyNotifiedRef.current = true;
      onReady?.();
    }
  }, [paymentRequest, onReady]);

  // Без списка зависимостей: тема воронки приезжает в remote config уже после
  // первого рендера, а перерисовка на его приход у этого компонента и так есть
  // (useRemoteConfig выше) — так цвет догоняет смену темы. Запись идемпотентная.
  const buttonRef = useRef(null);
  useEffect(() => {
    applyApplePayButtonStyle(buttonRef.current);
  });

  if (!paymentRequest) {
    return null;
  }

  const handleClickButton = () => {
    if (onClick) {
      onClick();
    }
    if (paymentSystem === PaymentSystem.RECURLY) {
      paymentRequest.begin();
    } else if (paymentSystem === PaymentSystem.STRIPE) {
      paymentRequest.show();
    } else if (paymentSystem === PaymentSystem.SOLIDGATE) {
      paymentRequest.begin();
    }
  };

  const buttonClassNames = classNames(
    classes["apple-pay-button"],
    {
      [classes["apple-pay-button-animation"]]: showApplePayAnimation,
    },
    className
  );

  return (
    <button
      ref={buttonRef}
      label="apple"
      type="button"
      disabled={disabled}
      className={buttonClassNames}
      onClick={handleClickButton}
    />
  );
};

ApplePayButton.propTypes = {
  product: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  className: PropTypes.any,
  onClick: PropTypes.func,
  onSubmit: PropTypes.func.isRequired,
  onReady: PropTypes.func,
};

ApplePayButton.defaultProps = {
  disabled: false,
  className: null,
  onClick: null,
  onReady: null,
};
